import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { categories, articles } from '@/mocks/siteData';
import SEOHead from '@/components/feature/SEOHead';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'xiazai-jiaocheng');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryArticles = articles.filter((a) => a.category === activeCategory);
  const categoryName = currentCategory?.name || '分类';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://pgzhengtu.com/' },
      { '@type': 'ListItem', position: 2, name: categoryName, item: `https://pgzhengtu.com/category/${activeCategory}` },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${categoryName} - PG征途国际下载安装`}
        description={currentCategory?.description || 'PG征途国际下载安装攻略指南'}
        canonicalUrl={`https://pgzhengtu.com/category/${activeCategory}`}
        schemaData={breadcrumbSchema}
      />

      <main className="pt-20 pb-14">
        {/* Breadcrumb */}
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium">{categoryName}</span>
          </nav>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              {categoryName}
            </h1>
            <p className="text-foreground-600 text-sm">
              {currentCategory?.description}
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} mr-1.5`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Article List */}
          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-200"
                >
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      {article.tags.slice(0, 2).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h2 className="font-heading font-semibold text-foreground-900 text-sm leading-snug mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span>{article.date}</span>
                      <span>{article.readTime}阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
                <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}